import React, { useMemo } from 'react';
import { amActions } from '../amStore';
import { COLORS } from '../amConfig';
import { monthLabel } from '../amRules';
import { groupBy, sortedMonths } from '../amMetrics';
import {
  Panel, ShareBar, Chart, clickTo, DrillTable, perfColumns, fmtInt, fmtPct,
} from '../amUi';

const DRILL_LEVELS = [
  { field: 'org', label: 'Organised status', filterKey: 'org' },
  { field: 'transporter', label: 'Transporter', filterKey: 'transporter' },
  { field: 'lane', label: 'Lane', filterKey: 'lane' },
];

const counts = (rows, field) => [...groupBy(rows, field).entries()]
  .map(([name, items]) => ({ name, count: items.length })).sort((a, b) => b.count - a.count);

function monthlyStack(rows, field, { percent = false, colors } = {}) {
  const months = sortedMonths(rows);
  const values = counts(rows, field).map((c) => c.name);
  const byMonth = new Map(months.map((m) => [m, {}]));
  rows.forEach((r) => { if (r.month) { const o = byMonth.get(r.month); o[r[field]] = (o[r[field]] || 0) + 1; } });
  return {
    chart: { type: 'column' },
    xAxis: { categories: months.map(monthLabel) },
    yAxis: percent ? { max: 100, endOnTick: false, title: { text: '% of shipments' }, labels: { format: '{value}%' } } : { title: { text: 'Shipments' } },
    plotOptions: { column: { stacking: percent ? 'percent' : 'normal' }, series: clickTo((cat, pt) => amActions.toggle('month', months[pt.index])) },
    tooltip: { shared: true, pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y:,.0f}</b> ({point.percentage:.1f}%)<br/>' },
    series: values.map((v, i) => ({
      name: v, data: months.map((m) => byMonth.get(m)[v] || 0),
      color: (colors && colors[v]) || COLORS.steel[i % COLORS.steel.length],
      events: { legendItemClick: () => false },
    })),
  };
}

export default function NetworkPage({ ctx }) {
  const { s, rows, drillPath } = ctx;
  const shares = useMemo(() => ({
    primSec: counts(rows, 'primSec'), type: counts(rows, 'type'), tptCat: counts(rows, 'tptCat'), org: counts(rows, 'org'),
  }), [rows]);
  const modeChart = useMemo(() => monthlyStack(rows, 'type'), [rows]);
  const catChart = useMemo(() => monthlyStack(rows, 'tptCat'), [rows]);
  const orgChart = useMemo(() => monthlyStack(rows, 'org', { percent: true, colors: { Organised: COLORS.steel[1], 'Unorganised / manual': COLORS.steel[4] } }), [rows]);
  const total = rows.length;
  const shareCol = {
    key: 'share', label: 'Share %', align: 'right', value: (r) => r.total,
    render: (r) => fmtPct(total ? (r.total / total) * 100 : null),
  };
  const shareBlocks = [
    { key: 'primSec', title: 'Primary vs secondary', note: 'Primary = destination pincode is a plant / warehouse source pincode' },
    { key: 'type', title: 'PTL vs FTL', filter: 'type' },
    { key: 'tptCat', title: 'Transport category' },
    { key: 'org', title: 'Organised vs unorganised', note: 'Organised = API-tracked transporters' },
  ];
  return (
    <>
      <div className="am-grid two">
        {shareBlocks.map((b) => (
          <Panel key={b.key} title={b.title} subtitle={b.note}>
            <ShareBar items={shares[b.key]} total={total} selected={s.filters[b.key]}
              onSelect={(v) => amActions.toggle(b.key, v)} />
          </Panel>
        ))}
      </div>
      <div className="am-grid two">
        <Panel title="Monthly shipments by mode" subtitle="PTL vs FTL · click a month to filter"><Chart options={modeChart} height={270} /></Panel>
        <Panel title="Monthly shipments by transport category"><Chart options={catChart} height={270} /></Panel>
      </div>
      <Panel title="Organised share by month" subtitle="100% of shipments in each dispatch month"><Chart options={orgChart} height={250} /></Panel>
      <Panel title="Network drill" subtitle="Organised status → transporter → lane → shipments">
        <DrillTable rows={rows} levels={DRILL_LEVELS} onLeaf={(path) => drillPath(path)}
          columnsFor={(label) => {
            const cols = perfColumns(label);
            cols.splice(2, 0, shareCol);
            return cols;
          }} />
      </Panel>
      <p className="am-note">{fmtInt(total)} shipments in view. Movement type is available as a filter only (mostly blank in the source) and is not used for primary / secondary.</p>
    </>
  );
}
